import { useNavigate, useRouterState } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { getAttribution } from "@/lib/attribution";

import { FIRM } from "@/lib/firm";
import { submitEnquiry } from "@/lib/enquiries.functions";
import { Button } from "@/components/ui/button";

const MATTER_OPTIONS = [
  { value: "conveyancing-purchase", label: "Buying a property" },
  { value: "conveyancing-sale", label: "Selling a property" },
  { value: "wills-estates", label: "Wills & estates" },
  { value: "elder-law", label: "Elder law / power of attorney" },
  { value: "other", label: "Something else" },
] as const;

export const HEARD_ABOUT_OPTIONS = [
  "Google search",
  "LinkedIn",
  "Facebook or Instagram",
  "Referred by a real estate agent",
  "Referred by a mortgage broker",
  "Referred by an accountant or adviser",
  "Referred by a friend or family member",
  "Existing or past client",
  "Other",
] as const;

const field =
  "w-full rounded-sm border border-input bg-card px-3 py-2.5 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-ring focus:ring-2 focus:ring-ring/20";
const labelCls = "block text-sm font-medium text-foreground";

export function EnquiryForm({
  heading = "Request a fixed-fee quote",
  suburb,
  audience = "client",
  defaultMatterType,
  defaultMessage,
}: {
  heading?: string;
  /** Pre-fills the suburb field on location pages so every lead is geo-tagged. */
  suburb?: string;
  audience?: "client" | "broker";
  defaultMatterType?: (typeof MATTER_OPTIONS)[number]["value"];
  defaultMessage?: string;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [matter, setMatter] = useState<string>(defaultMatterType ?? (audience === "broker" ? "other" : "conveyancing-purchase"));
  const estateMatter = ["wills-estates", "elder-law"].includes(matter);
  const conveyancingMatter = matter.startsWith("conveyancing");
  const [errors, setErrors] = useState<{ name?: string; email?: string; message?: string }>({});

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);

    const payload = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      phone: String(fd.get("phone") ?? ""),
      matterType: String(fd.get("matterType") ?? "conveyancing-purchase"),
      propertyAddress: String(fd.get("propertyAddress") ?? ""),
      suburb: String(fd.get("suburb") ?? ""),
      keyDate: String(fd.get("keyDate") ?? ""),
      message: String(fd.get("message") ?? ""),
      heardAbout: String(fd.get("heardAbout") ?? ""),
      ...getAttribution(pathname),
    };

    const nextErrors: { name?: string; email?: string; message?: string } = {};
    if (payload.name.trim().length < 2) nextErrors.name = "Please tell us your name";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(payload.email.trim()))
      nextErrors.email = "Please enter a valid email address";
    if (payload.message.trim().length < 10) nextErrors.message = "Please give us a little detail";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      const firstInvalid = nextErrors.name ? "name" : nextErrors.email ? "email" : "message";
      form.querySelector<HTMLElement>(`[name="${firstInvalid}"]`)?.focus();
      return;
    }

    setSending(true);
    try {
      await submitEnquiry({ data: payload as never });
      setSent(true);
      if (audience === "client") {
        void import("@/lib/google-ads").then((m) => m.reportEnquiryConversion());
      }
      form.reset();
      toast.success("Thanks — we've received your enquiry and will be in touch shortly.");
      void navigate({ to: "/thank-you", search: { type: "enquiry", matter: payload.matterType } });
    } catch {
      toast.error(`Something went wrong. Please call us on ${FIRM.phone}.`);
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="rounded-sm border border-border bg-card p-8 text-center">
        <h2 className="font-serif text-2xl text-foreground">Thank you</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          Your enquiry has reached us. We usually reply the same business day. If it's urgent, call{" "}
          <a href={FIRM.phoneHref} className="font-semibold text-primary underline">
            {FIRM.phone}
          </a>
          .
        </p>
        <Button
          type="button"
          onClick={() => setSent(false)}
          className="mt-6 text-sm text-muted-foreground underline"
        >
          Send another enquiry
        </Button>
      </div>
    );
  }

  return (
    <form
      id="enquiry"
      onSubmit={onSubmit}
      className="scroll-mt-24 rounded-sm border border-border bg-card p-6 sm:p-8"
      noValidate
      aria-busy={sending}
    >
      <h2 className="font-serif text-2xl text-foreground">{heading}</h2>
      <p className="mt-2 text-sm text-muted-foreground">
        {audience === "broker"
          ? "Introduce yourself and your brokerage, or ask about a Queensland property matter. Use your own contact details; please do not include client documents or personal information here."
          : estateMatter ? "Tell us whether you need a new will, an enduring power of attorney or help with an estate. Request a quote and the next steps, with no obligation." : "Buying or selling? Tell us about the property and any contract deadline. Request a fixed-fee quote, with no obligation."}
      </p>

      <p role="alert" className="sr-only">{Object.keys(errors).length ? "Please check the highlighted fields." : ""}</p>
      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="name">
            Your name
          </label>
          <input id="name" name="name" className={`${field} mt-2`} autoComplete="name" maxLength={120} required aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? "name-error" : undefined} />
          {errors.name && <p id="name-error" className="mt-1.5 text-xs text-destructive">{errors.name}</p>}
        </div>
        <div>
          <label className={labelCls} htmlFor="phone">
            Phone
          </label>
          <input id="phone" name="phone" className={`${field} mt-2`} autoComplete="tel" inputMode="tel" maxLength={40} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="email">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            className={`${field} mt-2`}
            autoComplete="email"
            maxLength={200}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? "email-error" : undefined}
            required
          />
          {errors.email && <p id="email-error" className="mt-1.5 text-xs text-destructive">{errors.email}</p>}
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="matterType">
            What do you need help with?
          </label>
          <select id="matterType" name="matterType" className={`${field} mt-2`} value={matter} onChange={e => setMatter(e.target.value)}>
            {MATTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="propertyAddress">
            Property address <span className="text-muted-foreground">(if relevant)</span>
          </label>
          <input id="propertyAddress" name="propertyAddress" className={`${field} mt-2`} maxLength={250} />
        </div>
        <div>
          <label className={labelCls} htmlFor="suburb">
            Suburb
          </label>
          <input
            id="suburb"
            name="suburb"
            className={`${field} mt-2`}
            maxLength={120}
            defaultValue={suburb ?? ""}
            placeholder="Golden Beach"
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="keyDate">
            Key date <span className="text-muted-foreground">(contract, settlement or meeting)</span>
          </label>
          <input id="keyDate" name="keyDate" type="date" className={`${field} mt-2`} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="heardAbout">
            How did you hear about us?
          </label>
          <select id="heardAbout" name="heardAbout" className={`${field} mt-2`} defaultValue={audience === "broker" ? "Referred by a mortgage broker" : ""}>
            <option value="">Please choose…</option>
            {HEARD_ABOUT_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="message">
            How can we help?
          </label>
          <textarea id="message" name="message" rows={5} maxLength={4000} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? "message-error" : undefined} className={`${field} mt-2`} defaultValue={defaultMessage} placeholder={audience === "broker" ? "Your brokerage, where you work, and whether you would like an introduction or help with a contract deadline. Please leave client names and confidential details out." : undefined} required />
          {errors.message && <p id="message-error" className="mt-1.5 text-xs text-destructive">{errors.message}</p>}
        </div>
      </div>

      <Button
        type="submit"
        disabled={sending}
        className="mt-6 h-auto whitespace-normal inline-flex w-full items-center justify-center rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60 sm:w-auto"
      >
        {sending ? "Sending…" : audience === "broker" ? "Send broker enquiry" : estateMatter ? "Request my wills & estate quote" : conveyancingMatter ? "Request my conveyancing quote" : "Send my enquiry"}
        {!sending && <ArrowRight className="size-4 shrink-0" aria-hidden="true" />}
      </Button>
      <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
        Sending an enquiry does not create a lawyer–client relationship. Please don't include
        confidential information until we've confirmed we can act for you.
      </p>
    </form>
  );
}
